/**
 * Customer data form configuration.
 *
 * Please see further documentation in the `src/types.ts` file.
 */
import { ConfigurableFormConfig, JSONSchema7Object } from "../../../types";
import { isPostCodeRequired } from "../../../utils/countries";
import { get } from "../../../utils/helpers";
import * as formElements from "../../shared/forms/formElements";
import formElementsSchema from "../../shared/forms/formElements.json";
import validateSchema from "./customerData.json";
import fredFormElementsSchema from "./formElements.json";

export const formConfig: ConfigurableFormConfig = {
  fields: [
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.personalInformation.title"
    },
    {
      ...formElements.group,
      id: "customer-name",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        not: {
          all: {
            has: {
              "pageState.language": "ja"
            }
          }
        }
      },
      fields: [
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.honorificPrefix.label",
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        }
      ]
    },
    {
      ...formElements.group,
      id: "customer-name-jp",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        all: {
          has: {
            "pageState.language": "ja"
          }
        }
      },
      fields: [
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.honorificPrefix.label",
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateFamilyName1",
          name: "alternateFamilyName1",
          label: "customer.additionalInfo.fields.globalLastName.label",
          props: {
            required: true
          },
          errorMessage:
            "customer.additionalInfo.fields.globalLastName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateGivenName1",
          name: "alternateGivenName1",
          label: "customer.additionalInfo.fields.globalFirstName.label",
          props: {
            required: true
          },
          errorMessage:
            "customer.additionalInfo.fields.globalFirstName.errors.required"
        }
      ]
    },
    {
      ...formElements.fieldCountry,
      id: "nationality",
      name: "nationality",
      label: "customer.data.fields.nationality.label",
      errorMessage: "customer.data.fields.nationality.errors.required"
    },
    {
      ...formElements.fieldBirthDate,
      id: "birthDate",
      name: "birthDate",
      label: "customer.data.fields.birthDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
          },
          minimumAge: 16
        }
      },
      stateToProps: {
        "pageState.language": "inputProps.lang",
        "appState.storeCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.birthDate.errors.required",
        minimumAge:
          "customer.data.fields.birthDate.options.year.errors.underMinimumAge"
      }
    },
    {
      ...formElements.fieldCustomPicklist,
      id: "maritalStatus",
      name: "maritalStatus",
      label: "customer.data.fields.maritalStatus.label",
      errorMessage: "customer.data.fields.maritalStatus.errors.required"
    },
    {
      ...formElements.fieldDate,
      id: "weddingDate",
      name: "weddingDate",
      label: "customer.data.fields.weddingDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
          },
          numYears: 100,
          startYear: new Date().getUTCFullYear() + 2
        }
      },
      errorMessage: "customer.data.fields.weddingDate.errors.invalid"
    },
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.contactDetails.title"
    },
    {
      ...formElements.fieldEmailDqe,
      id: "email",
      name: "email",
      label: "customer.data.fields.email.label",
      props: {
        inputProps: {
          maxLength: 250
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.emailConsent") ||
          get(input, "formState.isRemote")
      },
      errorMessage: {
        _: "customer.data.fields.email.errors.required",
        format: "customer.data.fields.email.errors.invalid"
      }
    },
    {
      ...formElements.fieldPhoneDqe,
      id: "mobileTelephone",
      name: "mobileTelephone",
      label: "customer.data.fields.mobilePhone.label",
      props: {
        hideLabel: true,
        inputProps: {
          searchable: true,
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.mobilePhone.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country",
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.phoneConsent") ||
          get(input, "formState.appsConsent")
      },
      errorMessage: {
        _: "customer.data.fields.phone.errors.required",
        pattern: "customer.data.fields.phone.dialCode.errors.required"
      }
    },
    {
      ...formElements.fieldPhoneDqe,
      id: "telephone",
      name: "telephone",
      label: "customer.data.fields.phone.label",
      props: {
        hideLabel: true,
        inputProps: {
          searchable: true,
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.phone.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country",
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.phoneConsent") ||
          get(input, "formState.appsConsent")
      },
      errorMessage: {
        _: "customer.data.fields.phone.errors.required",
        pattern: "customer.data.fields.phone.dialCode.errors.required"
      }
    },
    {
      ...formElements.fieldCustomPicklist,
      id: "instantMessageType",
      name: "instantMessageType",
      label: "customer.data.fields.instantMessageType.label"
      //errorMessage: "customer.data.fields.instantMessagingType.errors.required"
    },
    {
      ...formElements.fieldText,
      id: "instantMessageDescription",
      name: "instantMessageDescription",
      label: "customer.data.fields.instantMessageDescription.label",
      props: {
        enterInRoman: false,
        inputProps: {
          maxLength: 200
        }
      }
    },
    {
      ...formElements.fieldCountry,
      id: "addressCountry",
      name: "addressCountry",
      label: "customer.data.fields.address.country.label",
      errorMessage: "customer.data.fields.address.country.errors.required",
      props: {
        required: true,
        enterInRoman: true,
        inputProps: {
          maxLength: 35
        }
      }
    },
    {
      ...formElements.group,
      id: "customer-address",
      props: {
        className: "customer-address"
      },

      fields: [
        {
          ...formElements.fieldAutocompleteDqe,
          id: "streetAddress1Maps",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          showOnlyIf: {
            not: {
              has: {
                "formState.addressCountry": "JPN"
              }
            }
          },
          errorMessage: "customer.data.fields.address.line1.errors.required"
        },

        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          props: {
            inputProps: {
              maxLength: 35
            }
          },
          stateToProps: {
            "formState.postConsent": "required"
          },
          errorMessage: "customer.data.fields.address.city.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          props: {
            inputProps: {
              maxLength: 35
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            required: (value: any, propName?: string, input?: any) =>
              get(input, "formState.postConsent") &&
              get(input, "formState.addressCountry") &&
              isPostCodeRequired(get(input, "formState.addressCountry"))
          },
          errorMessage: "customer.data.fields.address.postCode.errors.required",
          showOnlyIf: {
            not: {
              has: {
                "formState.addressCountry": "JPN"
              }
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "postalCodeMaps",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          showOnlyIf: {
            has: {
              "formState.addressCountry": "JPN"
            }
          }
        }
      ]
    },
    {
      ...formElements.fieldCustomPicklist,
      id: "preferredContactChannel",
      name: "preferredContactChannel",
      label: "customer.data.fields.preferredContactChannel.label"
      //errorMessage: "customer.data.fields.preferredContactChannel.errors.required"
    },
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.communicationMethods.title"
    },
    {
      ...formElements.sectionText,
      label: "customer.data.sections.communicationMethods.intro"
    },
    {
      ...formElements.fieldCheckbox,
      id: "emailConsent",
      name: "emailConsent",
      label: "customer.data.fields.emailConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "postConsent",
      name: "postConsent",
      label: "customer.data.fields.postConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "phoneConsent",
      name: "phoneConsent",
      label: "customer.data.fields.phoneConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "appsConsent",
      name: "appsConsent",
      label: "customer.data.fields.appsConsent.label"
    }
  ],
  validateSchema: validateSchema as JSONSchema7Object,
  refSchemas: [
    formElementsSchema as JSONSchema7Object,
    fredFormElementsSchema as JSONSchema7Object
  ],
  labels: {
    buttonBack: "general.buttons.cancel.label",
    buttonNext: "general.buttons.continue.label",
    buttonSubmit: "customer.data.buttons.next.label"
  }
};

export default formConfig;
